import { memo, useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import {
  fetchDomainUsersListThunk,
  updateUserPermissionsThunk,
} from 'features/team/slice'
import { UpdateUserPermissionsPayload } from 'features/team/api'
import { useAppDispatch } from 'app/hooks'
import SuccessModal from 'components/Modal/SuccessModal'
import { RegularModerate } from 'components/Typography'
import { SpinnerCentered } from 'components/Loader'
import { SpinnerWrapper } from './styles'
import { Checkbox } from 'components/Input'
import { commonMessages } from 'app/i18n/messages'
import { teamPageMessages } from 'features/team/messages'
import { UPDATE_PERMISSIONS_ACTIONS } from 'features/team/consts'
import useCurrentSelectedDomainId from 'features/DomainsSelector/hooks'
import Tooltip from 'components/Tooltip'

interface PermissionCheckboxProps {
  updatePermissionsAction: UPDATE_PERMISSIONS_ACTIONS
  id: string
  isAdmin: boolean
  isConfigAuthor: boolean
  isSubscriptionManager: boolean
  disabled: boolean
  tooltipText?: string
}

function PermissionCheckbox({
  updatePermissionsAction,
  id: userId,
  isAdmin,
  isConfigAuthor,
  isSubscriptionManager,
  disabled,
  tooltipText,
}: PermissionCheckboxProps) {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const domainId = useCurrentSelectedDomainId()

  const [error, setError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const userPermissions = {
    [UPDATE_PERMISSIONS_ACTIONS.UPDATE_ADMIN]: isAdmin,
    [UPDATE_PERMISSIONS_ACTIONS.UPDATE_CONFIG]: isConfigAuthor,
    [UPDATE_PERMISSIONS_ACTIONS.UPDATE_SUBSCRIPTION_MANAGEMENT]:
      isSubscriptionManager,
  }
  const isChecked = userPermissions[updatePermissionsAction]

  const handleClick = useCallback(async () => {
    const permissions: UpdateUserPermissionsPayload = {
      domainPermissionDomain:
        updatePermissionsAction === UPDATE_PERMISSIONS_ACTIONS.UPDATE_ADMIN
          ? !isAdmin
          : isAdmin,
      domainPermissionUser:
        updatePermissionsAction === UPDATE_PERMISSIONS_ACTIONS.UPDATE_ADMIN
          ? !isAdmin
          : isAdmin,
      homePermissionSsh: true, // Must always give dealers SSH access. In the future Savant may wish to restrict this.
      configAuthor:
        updatePermissionsAction === UPDATE_PERMISSIONS_ACTIONS.UPDATE_CONFIG
          ? !isConfigAuthor
          : isConfigAuthor,
      // TODO: fix isSubscriptionManager when it will be implemented on backend
      // isSubscriptionManager: updatePermissionsAction === UPDATE_PERMISSIONS_ACTIONS.UPDATE_SUBSCRIPTION_MANAGEMENT ? !isSubscriptionManager : isSubscriptionManager,
    }

    try {
      setIsLoading(true)
      await dispatch(updateUserPermissionsThunk(userId, domainId, permissions))
      await dispatch(fetchDomainUsersListThunk(domainId))
    } catch (error) {
      console.log(
        `Error removing user ${userId} from domain  ${domainId}`,
        error
      )
      setError(error?.message)
    } finally {
      setIsLoading(false)
    }
  }, [
    dispatch,
    domainId,
    isAdmin,
    isConfigAuthor,
    updatePermissionsAction,
    userId,
  ])

  const Wrapper = ({ children }: { children: JSX.Element }) => {
    return tooltipText && children ? (
      <Tooltip showOn="click" text={tooltipText}>
        {children}
      </Tooltip>
    ) : (
      <>{children}</>
    )
  }

  return (
    <>
      {error && (
        <SuccessModal
          isOpen
          confirmBtnTitle={formatMessage(commonMessages.close)}
          title={formatMessage(teamPageMessages.permissionsUpdateError)}
          onConfirm={() => setError(false)}
        >
          <RegularModerate>
            {error || formatMessage(commonMessages.commonError)}
          </RegularModerate>
        </SuccessModal>
      )}

      {isLoading ? (
        <SpinnerWrapper>
          <SpinnerCentered size="20" />
        </SpinnerWrapper>
      ) : (
        <Wrapper>
          <Checkbox
            checked={isChecked}
            // TODO: fix isSubscriptionManager when it will be implemented on backend
            disabled={
              disabled ||
              isLoading ||
              updatePermissionsAction ===
                UPDATE_PERMISSIONS_ACTIONS.UPDATE_SUBSCRIPTION_MANAGEMENT
            }
            onChange={handleClick}
          />
        </Wrapper>
      )}
    </>
  )
}

export default memo(PermissionCheckbox)
